import React, { useState, useEffect, type ComponentProps } from 'react'
import { AddressManualEntryFieldsetStyled } from './AddressManualEntry.styled'
import { ValidationResult } from '../../../../component-library/src/types'
import {
  AutocompleteInputField,
  AutocompleteInputProps,
} from '@mobi/component-library/Common/Input/AutocompleteInputField/AutocompleteInputField'
import { Input } from '@mobi/component-library/Common/V2'
import { getRequiredValidationMessage } from '@mobi/account/Areas/SignUp/Utils'

export type ManualAddress = {
  unitNumber?: string
  streetNumber: string
  streetName: string
  streetType: string
  suburbStateAndPostcode: string
}

type FetchSuggestionsCallback = ComponentProps<
  typeof AutocompleteInputField
>['fetchSuggestionsCallback']

type AddressManualEntryProps = {
  inputNamePrefix?: string
  fetchStreetSuggestionsCallback: FetchSuggestionsCallback
  fetchSuburbSuggestionsCallback: FetchSuggestionsCallback
  onValidationChange?: (result: ValidationResult<ManualAddress>) => void
}

export const AddressManualEntry = ({
  inputNamePrefix = '',
  fetchStreetSuggestionsCallback,
  fetchSuburbSuggestionsCallback,
  onValidationChange,
}: AddressManualEntryProps) => {
  const [unitNumber, setUnitNumber] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [streetName, setStreetName] = useState('')
  const [streetType, setStreetType] = useState('')
  const [suburbStateAndPostcode, setSuburbStateAndPostcode] = useState('')
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  useEffect(() => {
    // TODO other validation, e.g. min length
    if (streetNumber && streetName && streetType && suburbStateAndPostcode) {
      onValidationChange?.({
        isValid: true,
        result: {
          unitNumber,
          streetNumber,
          streetName,
          streetType,
          suburbStateAndPostcode,
        },
      })
    } else {
      onValidationChange?.({
        isValid: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitNumber, streetNumber, streetName, streetType, suburbStateAndPostcode])

  return (
    <AddressManualEntryFieldsetStyled>
      <Input
        label='Unit Number'
        name={`${inputNamePrefix}unit-number`}
        placeholder='Enter your unit number'
        type='text'
        inputMode='text'
        value={unitNumber}
        onChange={e => setUnitNumber(e.currentTarget.value)}
      />
      <Input
        label='Street Number'
        name={`${inputNamePrefix}street-number`}
        placeholder='Enter your street number'
        type='number'
        inputMode='numeric'
        pattern='[0-9]*'
        value={streetNumber}
        onChange={e => setStreetNumber(e.currentTarget.value)}
        required
        onBlur={() => {
          setValidationMessages(prev => ({
            ...prev,
            streetNumber: getRequiredValidationMessage(streetNumber),
          }))
        }}
        isInvalid={!!validationMessages.streetNumber}
        helpText={validationMessages.streetNumber}
      />
      <Input
        label='Street Name'
        name={`${inputNamePrefix}street-name`}
        placeholder='Enter your street name'
        value={streetName}
        onChange={e => setStreetName(e.currentTarget.value)}
        required
        onBlur={() => {
          setValidationMessages(prev => ({
            ...prev,
            streetName: getRequiredValidationMessage(streetName),
          }))
        }}
        isInvalid={!!validationMessages.streetName}
        helpText={validationMessages.streetName}
      />
      <AutocompleteInputField
        inputName={`${inputNamePrefix}street-type`}
        name='Street Type'
        placeholder='Enter your street type'
        required
        minChars={2}
        fetchSuggestionsCallback={fetchStreetSuggestionsCallback}
        defaultValue={streetType}
        onSelect={setStreetType}
        onUnselect={() => setStreetType('')}
        renderInput={({
          inputName,
          name,
          ...props
        }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
          <Input
            {...props}
            name={inputName ?? 'autocomplete'}
            label={name}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                streetType: getRequiredValidationMessage(streetType),
              }))
            }}
            isInvalid={!!validationMessages.streetType}
            helpText={validationMessages.streetType}
          />
        )}
      />
      <AutocompleteInputField
        inputName={`${inputNamePrefix}suburb`}
        name='Suburb'
        placeholder='Enter your suburb'
        required
        fetchSuggestionsCallback={fetchSuburbSuggestionsCallback}
        defaultValue={suburbStateAndPostcode}
        onSelect={setSuburbStateAndPostcode}
        onUnselect={() => setSuburbStateAndPostcode('')}
        renderInput={({
          inputName,
          name,
          ...props
        }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
          <Input
            {...props}
            name={inputName ?? 'autocomplete'}
            label={name}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                suburbStateAndPostcode: getRequiredValidationMessage(suburbStateAndPostcode),
              }))
            }}
            isInvalid={!!validationMessages.suburbStateAndPostcode}
            helpText={validationMessages.suburbStateAndPostcode}
          />
        )}
      />
    </AddressManualEntryFieldsetStyled>
  )
}
